import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import U2000ComboBoxBase from '../../../formControls/combobox/U2000ComboBoxBase';

export interface U2000SearchBooleanOption {
  value: string;
  label: string;
  // Index signature requise pour satisfaire la contrainte générique de U2000ComboBoxBase<T extends Record<string, unknown>>
  [key: string]: unknown;
}

export function getSearchBooleanOptions(t: TFunction): U2000SearchBooleanOption[] {
  return [
    { value: 'true', label: t('lblSearchBooleanTrue') },
    { value: 'false', label: t('lblSearchBooleanFalse') },
  ];
}

/** Normalise une valeur critère vers `"true"` / `"false"` ou `null` si non reconnue. */
export function normalizeBooleanCriteriaValue(value: unknown): string | null {
  if (value === true || value === 'true' || value === '1') {
    return 'true';
  }
  if (value === false || value === 'false' || value === '0') {
    return 'false';
  }
  return null;
}

/** Libellé affiché pour un critère booléen (liste critères actifs, puces). */
export function formatBooleanCriteriaDisplayValue(value: unknown, t: TFunction): string {
  const normalized = normalizeBooleanCriteriaValue(value);
  if (normalized === 'true') {
    return t('lblSearchBooleanTrue');
  }
  if (normalized === 'false') {
    return t('lblSearchBooleanFalse');
  }
  return value != null && value !== '' ? String(value) : '';
}

interface U2000SearchBooleanValueComboProps {
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const U2000SearchBooleanValueCombo: React.FC<U2000SearchBooleanValueComboProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation('common');
  const options = useMemo(() => getSearchBooleanOptions(t), [t]);
  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  return (
    <U2000ComboBoxBase<U2000SearchBooleanOption>
      data={options}
      keyField="value"
      descField="label"
      value={selected}
      onChangeObject={(item) => onChange(item?.value ?? '')}
      disabled={disabled}
    />
  );
};

export default U2000SearchBooleanValueCombo;
